/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useLogsStore } from '@/ui/stores/logs-store'
import { Trash2, ArrowUp, ArrowDown, AlertCircle, Info } from 'lucide-react'

export function LogsPanel() {
  const { logs, clearLogs } = useLogsStore()

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    } as any)
  } // Correctly close formatTime function

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(6)}`
  }

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'request':
        return <ArrowUp className="w-3 h-3 text-blue-400" />
      case 'response':
        return <ArrowDown className="w-3 h-3 text-green-400" />
      case 'error':
        return <AlertCircle className="w-3 h-3 text-red-400" />
      case 'tool_call':
        return <span className="text-purple-400">🔧</span>
      default:
        return <Info className="w-3 h-3 text-foreground-muted" />
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-title text-foreground-secondary">
          LLM LOGS ({logs.length})
        </h3>
        <button
          onClick={clearLogs}
          className="p-1 hover:bg-background-tertiary rounded transition-colors"
          title="Clear all logs"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto space-y-1 text-[10px]">
        {logs.length === 0 ? (
          <div className="text-foreground-muted italic py-2">
            No logs yet. LLM requests will appear here.
          </div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              className="border border-border rounded p-1.5 hover:bg-background-hover transition-colors"
            >
              {/* Header Row */}
              <div className="flex items-center gap-1 mb-0.5">
                {getLogIcon(log.type)}
                <span className="text-foreground-muted">{formatTime(log.timestamp)}</span>
                <span className="text-accent font-semibold">{log.provider}</span>
                <span className="text-foreground-secondary truncate flex-1">{log.model}</span>
              </div>

              {/* Message */}
              <div className="text-foreground text-[9px] mb-0.5">
                {log.message}
              </div>

              {/* Tool Calls */}
              {log.toolCalls && log.toolCalls.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {log.toolCalls.map((toolCall, idx) => (
                    <div key={idx} className="bg-background-tertiary rounded px-1.5 py-0.5 text-[9px]">
                      <span className="text-purple-400 font-semibold">{toolCall.name}</span>
                      <span className="text-foreground-muted ml-1">
                        ({Object.keys(toolCall.args).join(', ')})
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Details */}
              {log.details && (
                <div className="flex items-center gap-2 text-foreground-muted text-[9px]">
                  {log.details.duration !== undefined && (
                    <span title="Duration">⏱️ {formatDuration(log.details.duration)}</span>
                  )}
                  {log.details.totalTokens !== undefined && (
                    <span title="Total tokens">🔢 {log.details.totalTokens}</span>
                  )}
                  {log.details.cost !== undefined && log.details.cost > 0 && (
                    <span title="Cost">💰 {formatCost(log.details.cost)}</span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

